/**
 * **广告日预算占比**（2026-10-06 · task-8 · 用户新需求）的唯一口径 —— 零依赖纯函数。
 *
 * ## 用户原话
 *
 * > 「按月的时候，在按店铺看的状态，增加 **广告预算占比**。举个例子，假设给店铺设置了
 * >  Meta占比15%，Google占比6%，则 Meta的日预算金额为：**最近30天销售额 * 0.15 / 30**。
 * >  可以设置 Meta和Google 的日预算比例。算出来日预算，显示在广告管理，然后按照
 * >  [三个规模分组] 分一下。并在每个组合计后边显示当前的投放比例。举例，现在ASC 日预算
 * >  2000美元，按照最近30天销售额算出来应该是2500美元/日，则 ASC日预算合计显示：
 * >  2000.00 （80%）代表当前日预算只投了规划的 80%。」
 *
 * ## 三条不可动摇的口径
 *
 *  1. **比例单位 = ppm（百万分比整数）**，与 `spend-overview.ts` 的占比单位一致
 *     （`15%` → `150000`）。整数运算避免浮点配置漂移。
 *  2. **NULL = 未配置，绝不折 0**：`sharePpm = null` → 规划日预算 `null` → 占比 `null`
 *     → 页面显示「—」；`sharePpm = 0` 是**真的配了 0%**（规划 = 0 → 分母为 0 → 同样「—」，
 *     但语义不同：一个是「没配」，一个是「配了 0」）。
 *  3. **跨币种绝不裸比**：销售额是**店铺币种**，广告日预算是**账户结算币种**。
 *     两者不同时必须用既有 fx 口径（`fx.ts` 的 `convertCents`）**显式换算**；
 *     缺汇率 / 汇率不可解析 → `fx_missing`，如实标「不可比」显示「—」，
 *     **绝不退化成 1:1、绝不用 `_usd_cents` 先折一次再折一次**（两次舍入）。
 *
 * ⚠️ **Meta% 与 Google% 是两条「各自占销售额」的独立比例，不是两者瓜分广告预算的份额**
 * （用户例子：Meta 15% + Google 6% = 21%）。因此：
 *  · 两者的规划日预算**分别** = `销售额 × 各自比例 ÷ 30`，**互不扣减**；
 *  · 相加**可以超过 100%**（例如 60% + 60%），也**不要求和为 1 / 归一化到 100%**；
 *  · 本模块**没有**、也**不得**引入任何「归一化 / 从总额里分一份」的函数。
 *  详见 `planPlatformDailyBudgetsShopCents` 与单测「两条比例互相独立、不归一化」。
 *
 * ## 窗口与销售额口径（复用，不另写）
 *
 *  · 「最近 30 天」= `settledObservationWindow({ latestFactDate, today, dayCount: 30 })`
 *    （`settled-window.ts` 的**唯一**观测窗口实现：不含今天、右端 = min(事实最新日, 今天−1)）；
 *  · 销售额 = `shopify_daily_metrics.total_revenue_cents`（**含税订单总额，店铺币种**，
 *    R34 口径；**不是** `total_revenue_usd_cents`）。
 *
 * ## 分组的定义不变
 *
 * 三个规模分组（`asc_main` / `cbo_main` / `test`）由 `tiers.ts` 的 `assignTiers` /
 * `summarizeTiers` 按 `scaleBasis`（日预算或消耗）的 Pareto 分档决定——**本模块不重算
 * 分组**，只把「每组实际日预算合计」接到规划日预算上算投放比例。用户例子：
 * ASC 实际 2000 ÷ 平台规划 2500 = 80% ⇒ **三个组共用同一分母**（该平台规划日预算），
 * 三组比例之和 = 整体投放率。
 */

import { convertCents, FX_SCALE_DIGITS, parseFxRateToScaled } from "./fx.js";
import type { Cents, CurrencyCode } from "./metrics.js";
import { settledObservationWindow, type SettledObservationWindow } from "./settled-window.js";
import type { TierGroupSummary, TierId } from "./tiers.js";

/** 比例单位：ppm = 百万分比（`15%` → `150000`）。与 `spend-overview.ts` 同一单位。 */
export const BUDGET_SHARE_PPM_SCALE = 1_000_000;

/** 比例上限（100%）。 */
export const BUDGET_SHARE_PPM_MAX = BUDGET_SHARE_PPM_SCALE;

/** 「最近 N 天销售额」的窗口天数（用户口径：最近 **30** 天）。 */
export const BUDGET_RATIO_REVENUE_WINDOW_DAYS = 30;

/** 平台维度（按店铺分别配置比例）。 */
export type BudgetPlatform = "meta" | "google";

/** 平台的稳定顺序（页面按此顺序展示）。 */
export const BUDGET_PLATFORMS: readonly BudgetPlatform[] = ["meta", "google"];

/** 平台展示名（唯一来源，避免各处各写一份）。 */
export const BUDGET_PLATFORM_LABELS: Readonly<Record<BudgetPlatform, string>> = {
  meta: "Meta",
  google: "Google",
};

/** 一个店铺的预算比例配置（对应 `shop_budget_ratios` 一行）。 */
export interface ShopBudgetRatioConfig {
  readonly shopId: string;
  /** Meta 比例（ppm 整数 0..1_000_000）；`null` = 未配置。 */
  readonly metaSharePpm: number | null;
  /** Google 比例（ppm 整数 0..1_000_000）；`null` = 未配置。 */
  readonly googleSharePpm: number | null;
  /** 最近一次写入的操作者（负责人）。 */
  readonly updatedBy: string;
  /** 最近一次写入时刻（ISO-8601）。 */
  readonly updatedAtIso: string;
}

/** 取某平台的比例（`null` = 未配置）。 */
export function sharePpmForPlatform(
  config: Pick<ShopBudgetRatioConfig, "metaSharePpm" | "googleSharePpm">,
  platform: BudgetPlatform,
): number | null {
  return platform === "meta" ? config.metaSharePpm : config.googleSharePpm;
}

/** 合法比例：整数且落在闭区间 `[0, 1_000_000]`。 */
export function isValidSharePpm(value: unknown): value is number {
  return (
    typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= BUDGET_SHARE_PPM_MAX
  );
}

/**
 * 百分比文本 → ppm。**非法输入返回 `null`（调用方必须拒绝，不得静默修正）**。
 *
 * 接受：`"15"` / `"6.5"` / `"15%"` / `"0.0001"` / `"100"`（小数最多 4 位 = 1 ppm 精度）；
 * 拒绝：空串、负数、科学计数法、超过 100%、超过 4 位小数、非数字。
 *
 * ⚠️ 本函数与 `formatSharePercentPpm` 是**全站唯一的 `% ↔ ppm` 换算对**：
 * 前端输入框、API 参数校验都只走它们，**不得**在别处各写 `× 10000` / `÷ 10000`。
 */
export function parseSharePercentToPpm(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  const matched = /^(\d{1,3})(?:\.(\d{1,4}))?%?$/.exec(trimmed);
  if (matched === null) return null;
  const whole = matched[1] ?? "";
  const fraction = (matched[2] ?? "").padEnd(4, "0");
  const ppm = Number(whole) * 10_000 + Number(fraction);
  return ppm <= BUDGET_SHARE_PPM_MAX ? ppm : null;
}

/**
 * ppm → 百分比文本（最多 4 位小数、去掉尾随 0）；`null` → 「—」。
 *
 * 与 `parseSharePercentToPpm` 互逆（同一精度约定），用于设置面板回显。
 * 非法 ppm（越界/非整数）→ **响亮抛错**（调用方 bug，不静默降级）。
 */
export function formatSharePercentPpm(ppm: number | null): string {
  if (ppm === null) return "—";
  if (!isValidSharePpm(ppm)) {
    throw new Error(`formatSharePercentPpm 需要 0..1000000 的整数 ppm，实际 ${String(ppm)}`);
  }
  const whole = Math.trunc(ppm / 10_000);
  const fraction = String(ppm % 10_000).padStart(4, "0").replace(/0+$/, "");
  return fraction === "" ? `${whole}%` : `${whole}.${fraction}%`;
}

/**
 * 「最近 30 天销售额」的**已结算观测窗口**——直接复用 `settledObservationWindow`
 * 的唯一实现（不含今天；右端 = min(事实最新业务日, 今天 − 1)）。
 *
 * 本函数存在的意义：把「30」这个口径固定在本模块，调用方（store / api）不需要
 * 自己记数字，也**不可能**写出第二套日期规则。
 */
export function budgetRatioRevenueWindow(input: {
  readonly latestFactDate: string | null;
  readonly today: string;
}): SettledObservationWindow {
  return settledObservationWindow({
    latestFactDate: input.latestFactDate,
    today: input.today,
    dayCount: BUDGET_RATIO_REVENUE_WINDOW_DAYS,
  });
}

/** 汇率缩放（10^10），与 `fx.ts` 的 `FX_SCALE_DIGITS` 一致。 */
const FX_SCALE = 10n ** BigInt(FX_SCALE_DIGITS);

/** 「1 X = 1 USD」的恒等汇率文本（`fx_rates` 对 USD 本身不存行）。 */
export const FX_IDENTITY_RATE_TEXT = "1";

/**
 * 由两条 **`X → USD`** 的 ECB 汇率派生 `base → quote` 的**交叉汇率**（十进制字符串，≤10 位小数）。
 *
 * 为什么需要它：`fx_rates` 只存**派生后的 `X → USD`** 一种形态（迁移 0015），
 * 而本需求要比较的两个币种可能都不含 USD（例如店铺 AUD、账户 GBP），或方向是
 * `USD → 本币`（店铺 USD、账户 AUD）。此时 `X → USD ÷ Y → USD` 就是同一个 ECB
 * 数据源下的唯一正确汇率（仍然是**既有 fx 口径**，只是换了个组合方式），
 * 不引入任何新数据源、也不做 1:1 回落。
 *
 * 任一侧缺汇率 / 不可解析 / ≤0 → `null`（调用方标 `fx_missing`）。
 * 精度：先按 10^10 缩放取整一次（与 `convertCents` 同一 `FX_SCALE_DIGITS`），
 * 再由 `convertCents` 对金额取整——两次取整的误差 ≤ 1 个 10^-10 相对量级。
 */
export function deriveCrossRateText(rateBaseToUsd: string, rateQuoteToUsd: string): string | null {
  const base = parseFxRateToScaled(rateBaseToUsd);
  const quote = parseFxRateToScaled(rateQuoteToUsd);
  if (base === null || quote === null || base <= 0n || quote <= 0n) return null;
  const scaled = (base * FX_SCALE + quote / 2n) / quote;
  if (scaled <= 0n) return null;
  const whole = scaled / FX_SCALE;
  const fraction = String(scaled % FX_SCALE).padStart(FX_SCALE_DIGITS, "0").replace(/0+$/, "");
  return fraction === "" ? whole.toString() : `${whole.toString()}.${fraction}`;
}

/**
 * 由两条 `X → USD` 汇率派生 `shopCurrency → accountCurrency` 的换算依据。
 *
 *  · 同币种 → `null`（**调用方不需要换算**；注意这与「缺汇率」不是一回事，
 *    是否可比由 `planDailyBudgetInAccountCurrency` 决定）；
 *  · 缺任一侧（且该侧不是 USD）→ `null`（调用方标 `fx_missing`，**绝不 1:1**）；
 *  · USD 侧用恒等（`fx_rates` 不存 USD 行）。
 */
export function deriveBudgetConversionRate(input: {
  readonly shopCurrency: CurrencyCode;
  readonly accountCurrency: CurrencyCode;
  /** `shopCurrency → USD` 的汇率文本；`shopCurrency === "USD"` 时传 `null`。 */
  readonly shopToUsdRate: string | null;
  /** `accountCurrency → USD` 的汇率文本；`accountCurrency === "USD"` 时传 `null`。 */
  readonly accountToUsdRate: string | null;
}): { readonly rate: string } | null {
  if (input.shopCurrency === input.accountCurrency) return null;
  const shopRate = input.shopCurrency === "USD" ? FX_IDENTITY_RATE_TEXT : input.shopToUsdRate;
  const accountRate = input.accountCurrency === "USD" ? FX_IDENTITY_RATE_TEXT : input.accountToUsdRate;
  if (shopRate === null || accountRate === null) return null;
  if (parseFxRateToScaled(shopRate) === null) return null;
  if (accountRate === FX_IDENTITY_RATE_TEXT) return { rate: shopRate };
  const cross = deriveCrossRateText(shopRate, accountRate);
  return cross === null ? null : { rate: cross };
}

/** 规划日预算输入（销售额与比例都用「未配置 / 未知 = null」表达）。 */
export interface PlanDailyBudgetInput {
  /** 窗口内销售额合计（**店铺币种**整数分）；`null` = 读不到（未知，**不是 0**）。 */
  readonly revenueCents: Cents | null;
  /** 配置比例（ppm 整数 0..1_000_000）；`null` = 未配置。 */
  readonly sharePpm: number | null;
  /** 窗口天数（与销售额同一窗口，必须 ≥ 1）。 */
  readonly dayCount: number;
}

/** 四舍五入（半程远离零；本模块的分子恒非负，等价于四舍五入）。 */
function divideRoundedHalfUp(numerator: bigint, denominator: bigint): bigint {
  if (denominator <= 0n) throw new Error(`divideRoundedHalfUp 的分母必须为正，实际 ${denominator.toString()}`);
  return (numerator + denominator / 2n) / denominator;
}

/**
 * **规划日预算（店铺币种）** = 窗口销售额 × 比例 ÷ 窗口天数。
 *
 * 全程 **BigInt 整数运算**，最后才取整一次（不会出现「按天先取整再乘」的漂移）；
 * 返回整数分。`null` 的两种来源严格区分：
 *  · `sharePpm === null` → 未配置；
 *  · `revenueCents === null` → 销售额未知（读不到事实行）。
 * 两者的**结果都是 `null`（不是 0）**，由 `planDailyBudgetInAccountCurrency` 翻译成状态。
 *
 * @throws 比例非法（非整数 / 越界）、销售额非法（非安全整数 / 负数）、天数非法。
 */
export function planDailyBudgetCents(input: PlanDailyBudgetInput): Cents | null {
  if (input.sharePpm === null || input.revenueCents === null) return null;
  if (!Number.isSafeInteger(input.dayCount) || input.dayCount < 1) {
    throw new Error(`planDailyBudgetCents 的 dayCount 必须是 ≥1 的安全整数，实际 ${String(input.dayCount)}`);
  }
  if (!Number.isSafeInteger(input.revenueCents) || input.revenueCents < 0) {
    throw new Error(
      `planDailyBudgetCents 的 revenueCents 必须是 ≥0 的安全整数分，实际 ${String(input.revenueCents)}`,
    );
  }
  if (!isValidSharePpm(input.sharePpm)) {
    throw new Error(
      `planDailyBudgetCents 的 sharePpm 必须是 0..${String(BUDGET_SHARE_PPM_MAX)} 的整数，实际 ${String(input.sharePpm)}`,
    );
  }
  const numerator = BigInt(input.revenueCents) * BigInt(input.sharePpm);
  const denominator = BigInt(input.dayCount) * BigInt(BUDGET_SHARE_PPM_SCALE);
  const planned = divideRoundedHalfUp(numerator, denominator);
  if (planned > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new Error(`规划日预算超出安全整数范围：${planned.toString()} 分`);
  }
  return Number(planned);
}

/**
 * 一个店铺**两个平台各自**的规划日预算（店铺币种）。
 *
 * ⚠️ 关键语义：Meta% 与 Google% 是**各自占销售额**的独立比例，**不是**瓜分同一笔广告预算。
 * 因此这里分别调用 `planDailyBudgetCents`（同一个销售额、各自的比例），**互不扣减、
 * 不做归一化**：`meta=15% + google=6%` ⇒ 两者之和 = 销售额 × 21% ÷ 30（可以超过 100%）。
 * 本模块刻意**不提供**任何把两条比例缩放到合计 100% 的函数。
 */
export function planPlatformDailyBudgetsShopCents(input: {
  readonly config: Pick<ShopBudgetRatioConfig, "metaSharePpm" | "googleSharePpm">;
  readonly revenueCents: Cents | null;
  readonly dayCount: number;
}): Readonly<Record<BudgetPlatform, Cents | null>> {
  return {
    meta: planDailyBudgetCents({
      revenueCents: input.revenueCents,
      sharePpm: input.config.metaSharePpm,
      dayCount: input.dayCount,
    }),
    google: planDailyBudgetCents({
      revenueCents: input.revenueCents,
      sharePpm: input.config.googleSharePpm,
      dayCount: input.dayCount,
    }),
  };
}

/** 规划日预算的可得性状态（**页面据此决定显示数字还是「—」**）。 */
export type PlannedBudgetStatus =
  /** 拿到数字（`plannedDailyBudgetCents` 非 null）。 */
  | "ok"
  /** 该店铺/平台未配置比例 → 显示「—」。 */
  | "unconfigured"
  /** 配置了比例但窗口内销售额读不到 → 显示「—」。 */
  | "revenue_unknown"
  /** 跨币种但缺汇率（或汇率不可解析）→ **不可比** → 显示「—」。 */
  | "fx_missing";

/** 规划日预算（已尽量换算到账户结算币种）的结果。 */
export interface PlannedDailyBudgetInAccountCurrency {
  readonly status: PlannedBudgetStatus;
  /** 账户结算币种的规划日预算；仅 `status === "ok"` 时非 null。 */
  readonly plannedDailyBudgetCents: Cents | null;
  /** 换算前的规划日预算（店铺币种）；用于解释 / 排障，可为 null。 */
  readonly plannedDailyBudgetShopCents: Cents | null;
  readonly shopCurrency: CurrencyCode;
  readonly accountCurrency: CurrencyCode;
  /** `true` = 发生了显式跨币种换算（同币种恒 false）。 */
  readonly converted: boolean;
}

export interface PlanDailyBudgetInAccountCurrencyInput extends PlanDailyBudgetInput {
  readonly shopCurrency: CurrencyCode;
  readonly accountCurrency: CurrencyCode;
  /**
   * `1 shopCurrency = rate accountCurrency` 的汇率（十进制字符串，取自既有 fx 口径）；
   * 同币种时忽略；跨币种且为 `null` → `fx_missing`。
   */
  readonly conversion: { readonly rate: string } | null;
}

/**
 * 规划日预算 → **账户结算币种**（跨币种红线的落点）。
 *
 *  · 同币种：直接用；
 *  · 跨币种且给了汇率：`convertCents` 显式换算（缺汇率 / 汇率不可解析 → `null`）；
 *  · 跨币种但没给汇率：`fx_missing`（**绝不 1:1**）。
 */
export function planDailyBudgetInAccountCurrency(
  input: PlanDailyBudgetInAccountCurrencyInput,
): PlannedDailyBudgetInAccountCurrency {
  const shopPlanned = planDailyBudgetCents(input);
  const base = {
    shopCurrency: input.shopCurrency,
    accountCurrency: input.accountCurrency,
    plannedDailyBudgetShopCents: shopPlanned,
  } as const;

  if (shopPlanned === null) {
    return {
      ...base,
      status: input.sharePpm === null ? "unconfigured" : "revenue_unknown",
      plannedDailyBudgetCents: null,
      converted: false,
    };
  }
  if (input.shopCurrency === input.accountCurrency) {
    return { ...base, status: "ok", plannedDailyBudgetCents: shopPlanned, converted: false };
  }
  if (input.conversion === null) {
    return { ...base, status: "fx_missing", plannedDailyBudgetCents: null, converted: false };
  }
  const convertedCents = convertCents(shopPlanned, input.conversion);
  if (convertedCents === null) {
    return { ...base, status: "fx_missing", plannedDailyBudgetCents: null, converted: false };
  }
  return { ...base, status: "ok", plannedDailyBudgetCents: convertedCents, converted: true };
}

/**
 * **投放比例** = 实际日预算 ÷ 规划日预算，单位 ppm 整数。
 *
 *  · 规划 `null`（未配置 / 销售额未知 / 缺汇率）→ `null`；
 *  · 规划 `0`（配了 0%，或销售额 0）→ **`null`（分母为 0，绝不显示 0%/∞）**；
 *  · 实际 `null`（未知）→ `null`；
 *  · 实际 `0` → `0`（真实的「一点没投」，是合法结果）。
 *
 * @throws 实际为负数 / 非整数分（金额恒为非负整数分）。
 */
export function budgetAttainmentPpm(input: {
  readonly actualDailyBudgetCents: Cents | null;
  readonly plannedDailyBudgetCents: Cents | null;
}): number | null {
  const actual = input.actualDailyBudgetCents;
  const planned = input.plannedDailyBudgetCents;
  if (actual === null || planned === null) return null;
  if (!Number.isSafeInteger(actual) || actual < 0) {
    throw new Error(`budgetAttainmentPpm 的 actualDailyBudgetCents 必须是 ≥0 的整数分，实际 ${String(actual)}`);
  }
  if (!Number.isSafeInteger(planned)) {
    throw new Error(`budgetAttainmentPpm 的 plannedDailyBudgetCents 必须是整数分，实际 ${String(planned)}`);
  }
  if (planned <= 0) return null;
  const ppm = divideRoundedHalfUp(BigInt(actual) * BigInt(BUDGET_SHARE_PPM_SCALE), BigInt(planned));
  if (ppm > BigInt(Number.MAX_SAFE_INTEGER)) return null;
  return Number(ppm);
}

/** 某个「币种 × 三分组」合计行的实际日预算 + 投放比例（页面合计行直接用）。 */
export interface TierBudgetAttainment {
  readonly currency: CurrencyCode;
  readonly tier: TierId;
  /** 该组实际日预算合计（同币种、`scaleBasis` 口径，来自 `summarizeTiers`）。 */
  readonly actualDailyBudgetCents: Cents;
  /** 该币种下的规划日预算；`null` = 不可得（未配置 / 销售额未知 / 缺汇率）。 */
  readonly plannedDailyBudgetCents: Cents | null;
  /** 投放比例（ppm）；`null` → 页面显示「—」。 */
  readonly attainmentPpm: number | null;
  readonly plannedStatus: PlannedBudgetStatus;
  /** `true` = 规划值发生过跨币种换算（展示时可提示口径）。 */
  readonly converted: boolean;
}

/**
 * 把 `summarizeTiers` 的三分组合计接到规划日预算上（**不重算分组、不改既有列**）。
 *
 * 用户例子：ASC 实际 2000、平台规划 2500 → `attainmentPpm = 800000`（80%）。
 * 三个组**共用同一分母**（同币种的平台规划日预算），因此三组比例之和 = 整体投放率。
 */
export function attachTierBudgetAttainment(input: {
  readonly groups: readonly TierGroupSummary[];
  /** 每个账户结算币种一份规划日预算（由 `planDailyBudgetInAccountCurrency` 产出）。 */
  readonly plansByCurrency: readonly {
    readonly currency: CurrencyCode;
    readonly plan: PlannedDailyBudgetInAccountCurrency;
  }[];
}): readonly TierBudgetAttainment[] {
  const plans = new Map<CurrencyCode, PlannedDailyBudgetInAccountCurrency>();
  for (const entry of input.plansByCurrency) plans.set(entry.currency, entry.plan);

  return input.groups.map((group) => {
    const plan = plans.get(group.currency);
    const planned = plan?.plannedDailyBudgetCents ?? null;
    return {
      currency: group.currency,
      tier: group.tier,
      actualDailyBudgetCents: group.scaleTotal,
      plannedDailyBudgetCents: planned,
      attainmentPpm: budgetAttainmentPpm({
        actualDailyBudgetCents: group.scaleTotal,
        plannedDailyBudgetCents: planned,
      }),
      plannedStatus: plan?.status ?? "unconfigured",
      converted: plan?.converted ?? false,
    };
  });
}
